import React from "react";
import { PlatformIcon } from "./PlatformIcons";

interface ReviewsStatsChartProps {
  platformDistribution: { [key: string]: number };
  sentimentDistribution: { [key: string]: number };
  totalReviews: number;
}

const ReviewsStatsChart: React.FC<ReviewsStatsChartProps> = ({
  platformDistribution,
  sentimentDistribution,
  totalReviews,
}) => {
  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "wildberries":
        return "bg-gradient-to-r from-purple-400 to-purple-600";
      case "ozon":
        return "bg-gradient-to-r from-blue-400 to-blue-600";
      case "yandex":
        return "bg-gradient-to-r from-yellow-400 to-amber-500";
      default:
        return "bg-gradient-to-r from-gray-400 to-gray-600";
    }
  };

  const getPlatformName = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "wildberries":
        return "Wildberries";
      case "ozon":
        return "Ozon";
      case "yandex":
        return "Yandex";
      default:
        return platform;
    }
  };

  const getPlatformDescription = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "wildberries":
        return "Маркетплейс модной одежды и товаров";
      case "ozon":
        return "Универсальный онлайн-магазин";
      case "yandex":
        return "Отзывы магазина на Маркете";
      default:
        return "Торговая платформа";
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="text-xl font-bold text-gray-900">
          Статистика по платформам
        </h3>
      </div>

      <div className="space-y-6">
        {Object.entries(platformDistribution).map(([platform, count]) => {
          const percentage =
            totalReviews > 0 ? (count / totalReviews) * 100 : 0;
          const maxCount = Math.max(...Object.values(platformDistribution));
          const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;

          return (
            <div
              key={platform}
              className="group hover:bg-gray-50 rounded-xl p-3 transition-all duration-300 border border-gray-100 hover:border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-9 h-9 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                    <PlatformIcon platform={platform} className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">
                      {getPlatformName(platform)}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {getPlatformDescription(platform)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900">
                    {percentage.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-500">{count} отзывов</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Доля отзывов</span>
                  <span className="font-medium text-gray-900">
                    {count} из {totalReviews}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full ${getPlatformColor(platform)} transition-all duration-700 ease-out`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Общая статистика */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
            <div className="text-2xl font-bold text-blue-900 mb-1">
              {totalReviews}
            </div>
            <div className="text-xs text-blue-700 font-medium">
              Всего отзывов
            </div>
          </div>
          <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
            <div className="text-2xl font-bold text-purple-900 mb-1">
              {Object.keys(platformDistribution).length}
            </div>
            <div className="text-xs text-purple-700 font-medium">
              Активных платформ
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsStatsChart;
